import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DeviceEntryProps,
  FIRMWARE_KINDS,
  Firmware,
  FirmwareKind,
  FirmwareProps,
  FirmwareType,
  ProfileType,
  firmwareTypeForDevice,
} from '@ixicare/resources';
import { Alert, Button, Modal, RadioButtonGroup, Select, Spinner, TabsPanel } from '@ixicare/ui';
import { ArrowRight } from 'lucide-react';
import { t } from '~i18n';
import { useStores } from '~store/utils';
import { humaniseErrors } from '~utils/error';
import { useFirmwares } from '../hooks/useFirmwares';
import { OTA_TASKS_QUERY_KEY } from '../hooks/useOtaTasks';
import { firmwareDevicesLabel, firmwareKindLabel, plainVersion } from '../utils/firmwareLabels';
import { DeviceFirmwareStatus } from './DeviceFirmwareStatus';
import styles from './UpdateFirmwareModal.module.css';

const SPREAD_HOURS = 24;

type Timing = 'now' | 'spread';

interface Props {
  devices: DeviceEntryProps[];
  /** The tab to open on, when the devices take both software and firmware updates. */
  initialKind?: FirmwareKind;
  /**
   * Devices with an update that has not finished or failed. Planning replaces it, except while the
   * device is downloading or installing, which the backend refuses.
   */
  plannedDeviceIds?: Set<number>;
  onDevicesChanged?: () => void;
  onClose: () => void;
}

const byVersionDesc = (a: FirmwareProps, b: FirmwareProps) =>
  b.version.localeCompare(a.version, undefined, { numeric: true });

const MAX_LISTED_VERSIONS = 3;

const reportedVersion = (device: DeviceEntryProps, kind: FirmwareKind) =>
  kind === 'firmware' ? device.fwVersion : plainVersion(device.swVersion);

// "1.0, 1.2" for what a group of devices runs now, "—" when none reported a version.
const currentVersionsLabel = (devices: DeviceEntryProps[], kind: FirmwareKind) => {
  const versions = [...new Set(devices.map((device) => reportedVersion(device, kind)).filter(Boolean))].sort((a, b) =>
    a!.localeCompare(b!, undefined, { numeric: true })
  );
  if (!versions.length) return '—';
  return versions.length > MAX_LISTED_VERSIONS ? t('firmware.update.severalVersions') : versions.join(', ');
};

/**
 * Software or firmware updates of one or more devices: plans an update, and for a single device also
 * shows what it runs and how its last update went. Only the ixi Pro takes both kinds, so the modal
 * shows a tab per kind only when the selection holds one.
 */
export const UpdateFirmwareModal = ({ devices, initialKind, onClose, ...rest }: Props) => {
  const kinds = React.useMemo(
    () => FIRMWARE_KINDS.filter((kind) => devices.some((device) => firmwareTypeForDevice(device.deviceType, kind))),
    [devices]
  );
  const [activeKind, setActiveKind] = React.useState<FirmwareKind>(
    initialKind && kinds.includes(initialKind) ? initialKind : (kinds[0] ?? 'software')
  );
  const singleDevice = devices.length === 1 ? devices[0] : undefined;

  const panel = (kind: FirmwareKind) => (
    <UpdatePanel key={kind} kind={kind} devices={devices} onClose={onClose} {...rest} />
  );

  return (
    <Modal center>
      <h3 className="text-center mb-10">
        {activeKind === 'firmware' ? t('firmware.update.title') : t('firmware.update.titleSoftware')}
      </h3>
      {singleDevice && (
        <p className={styles.Device}>
          {singleDevice.description
            ? `${singleDevice.description} (${singleDevice.macAddress})`
            : singleDevice.macAddress}
        </p>
      )}
      {kinds.length > 1 ? (
        <div className={styles.Tabs}>
          <TabsPanel
            defaultOpenId={activeKind}
            onChange={(id) => setActiveKind(id as FirmwareKind)}
            tabs={kinds.map((kind) => ({ id: kind, label: firmwareKindLabel(kind), content: panel(kind) }))}
          />
        </div>
      ) : (
        panel(activeKind)
      )}
      <Button isBlock buttonType="link" onClick={onClose}>
        {t('global.cancel')}
      </Button>
    </Modal>
  );
};

interface PanelProps extends Omit<Props, 'initialKind'> {
  kind: FirmwareKind;
}

/**
 * A selection can mix modules (an ixi Lite and an ixi Touch need different software), so a version
 * is picked per firmware type, starting at the one marked latest. Only Ixicare employees pick a
 * version or spread the updates; everyone else updates to the latest, now.
 */
const UpdatePanel = ({ kind, devices, plannedDeviceIds, onDevicesChanged, onClose }: PanelProps) => {
  const { notifications, auth } = useStores();
  const isEmployee = auth.userType === ProfileType.WatcherrEmployee;
  const queryClient = useQueryClient();
  const { data: firmwares, isLoading } = useFirmwares();
  const [timing, setTiming] = React.useState<Timing>('now');
  const [chosen, setChosen] = React.useState<Partial<Record<FirmwareType, string>>>({});

  const groups = React.useMemo(() => {
    const byType = new Map<FirmwareType, DeviceEntryProps[]>();
    devices.forEach((device) => {
      const type = firmwareTypeForDevice(device.deviceType, kind);
      if (type) byType.set(type, [...(byType.get(type) ?? []), device]);
    });
    return [...byType.entries()].map(([type, members]) => {
      const versions = (firmwares ?? [])
        .filter((firmware) => firmware.firmwareType === type && (isEmployee || firmware.isLatest))
        .sort(byVersionDesc);
      const suggested = versions.find((firmware) => firmware.isLatest) ?? versions[0];
      return { type, devices: members, versions, suggested };
    });
  }, [devices, firmwares, isEmployee, kind]);

  const unsupported = devices.filter((device) => !firmwareTypeForDevice(device.deviceType, kind));
  const firmwareIdFor = (group: (typeof groups)[number]) => chosen[group.type] ?? group.suggested?.id;
  const schedulable = groups.filter((group) => firmwareIdFor(group));
  const singleDevice = devices.length === 1 ? devices[0] : undefined;
  const replacedCount = devices.filter((device) => plannedDeviceIds?.has(device.id)).length;

  const scheduleMutation = useMutation({
    mutationFn: () => {
      const executeFrom = new Date();
      const executeUntil =
        timing === 'spread' ? new Date(executeFrom.getTime() + SPREAD_HOURS * 60 * 60 * 1000) : undefined;
      return Promise.all(
        schedulable.map((group) =>
          Firmware.scheduleDeviceUpdates({
            deviceIds: group.devices.map((device) => device.id),
            firmwareId: firmwareIdFor(group)!,
            executeFrom,
            executeUntil,
          })
        )
      );
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: OTA_TASKS_QUERY_KEY });
      notifications.addUserActionNotification({
        type: 'success' as any,
        notificationType: 'success',
        message: t('firmware.update.success', { count: results.flat().length }),
      });
      onClose();
    },
  });

  return (
    <>
      {scheduleMutation.error && <Alert type="error">{humaniseErrors(scheduleMutation.error as Error).title}</Alert>}
      {isLoading ? (
        <Spinner />
      ) : (
        <div className={styles.Body}>
          {singleDevice && groups[0] && (
            <DeviceFirmwareStatus
              device={singleDevice}
              kind={kind}
              latest={groups[0].suggested}
              onDeviceChanged={onDevicesChanged}
            />
          )}
          {groups.map((group) => (
            <div key={group.type} className={styles.Group}>
              {!singleDevice && (
                <span className={styles.GroupTitle}>
                  {firmwareDevicesLabel(group.type)}
                  <span className={styles.GroupCount}>
                    {t('firmware.update.deviceCount', { count: group.devices.length })}
                  </span>
                </span>
              )}
              {!group.versions.length ? (
                <p className={styles.Muted}>
                  {kind === 'firmware' ? t('firmware.update.noFirmware') : t('firmware.update.noSoftware')}
                </p>
              ) : !isEmployee ? (
                <div className={styles.Transition}>
                  <span>{currentVersionsLabel(group.devices, kind)}</span>
                  <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
                  <span className={styles.ToVersion}>{group.suggested!.version}</span>
                  <span className={styles.LatestBadge}>{t('firmware.latest')}</span>
                </div>
              ) : (
                <Select
                  as="input"
                  label={t('firmware.version')}
                  value={firmwareIdFor(group)}
                  onChange={(id) => setChosen((prev) => ({ ...prev, [group.type]: id }))}
                  options={group.versions.map((firmware) => ({
                    value: firmware.id,
                    label: firmware.isLatest ? `${firmware.version} (${t('firmware.latest')})` : firmware.version,
                  }))}
                />
              )}
            </div>
          ))}
          {unsupported.length > 0 && (
            <Alert type="warning">
              {t(kind === 'firmware' ? 'firmware.update.unsupportedFirmware' : 'firmware.update.unsupported', {
                devices: unsupported.map((device) => device.description || device.macAddress).join(', '),
              })}
            </Alert>
          )}
          {schedulable.length > 0 && (
            <div className={styles.Timing}>
              {isEmployee && (
                <RadioButtonGroup
                  name={`firmware-update-timing-${kind}`}
                  value={timing}
                  onChange={setTiming}
                  options={[
                    { value: 'now', label: t('firmware.update.timing.now') },
                    { value: 'spread', label: t('firmware.update.timing.spread', { hours: SPREAD_HOURS }) },
                  ]}
                />
              )}
              <p className={styles.Muted}>
                {timing === 'now'
                  ? t('firmware.update.timing.nowHint')
                  : t('firmware.update.timing.spreadHint', { hours: SPREAD_HOURS })}
              </p>
            </div>
          )}
          {replacedCount > 0 && (
            <Alert type="warning">
              {singleDevice
                ? t('firmware.update.replacesPlannedSingle')
                : t('firmware.update.replacesPlanned', { count: replacedCount })}
            </Alert>
          )}
        </div>
      )}
      <Button
        className="mb-15"
        isBlock
        buttonType="success"
        type="button"
        disabled={!schedulable.length || scheduleMutation.isPending}
        loading={scheduleMutation.isPending}
        onClick={() => scheduleMutation.mutate()}
      >
        {t('firmware.update.submit', { count: schedulable.reduce((sum, group) => sum + group.devices.length, 0) })}
      </Button>
    </>
  );
};
